import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { z } from 'zod';
import { getIconUrl, getServiceColor, getSemanticType } from './aws-icon-registry';
import { escapeXml, safeHex } from './svgSafe';

const awsResourceProps = z.object({
  name: z.string().default(''),
  resourceType: z.string().default('resource'),
  semanticType: z.string().default('artifact'),
  resourceId: z.string().default(''),
  service: z.string().default(''),
  hostingProvider: z.string().default('aws'),
  managedService: z.string().optional(),
  region: z.string().optional(),
  az: z.string().optional(),
  encrypted: z.boolean().optional(),
  publicExposure: z.enum(['none', 'restricted', 'open']).optional(),
  monthlySpend: z.number().optional(),
  fillColor: z.string().default('#FFFFFF'),
  strokeColor: z.string().default('#94a3b8'),
  textColor: z.string().default('#1e293b'),
  accentColor: z.string().optional(),
  iconUrl: z.string().optional(),
  // M1 stack primitive: when this shape stands for a cluster of resources,
  // the renderer draws layered cards behind the primary card with a count
  // badge. Set `count` > 1 to enable; absent or 1 = single-card rendering.
  stack: z.object({ count: z.number().int().nonnegative() }).optional(),
});

export type AwsResourceProps = z.infer<typeof awsResourceProps>;

function renderAwsNodeSvg(
  viewModel: { width?: number; height?: number },
  props: Record<string, unknown>,
  isContainer: boolean,
): string {
  const w = viewModel.width ?? 160;
  const h = viewModel.height ?? 60;
  const name = String(props.name || props.resourceType || 'Resource');
  const rType = String(props.resourceType || '');
  // Sanitize every value that lands in an SVG attribute or text node.
  // `name` and `rType` may carry arbitrary user input from imports;
  // `*Color` props could be set programmatically — validate as hex, fall
  // back to defaults if not. See `svgSafe.ts` for the threat model.
  const accent = safeHex(props.accentColor, getServiceColor(rType));
  const fill = safeHex(props.fillColor, '#FFFFFF');
  const stroke = safeHex(props.strokeColor, '#94a3b8');
  const textColor = safeHex(props.textColor, '#1e293b');
  const escapedName = escapeXml(name);
  const escapedRType = escapeXml(rType);
  const escapedRTypeUpper = escapeXml(rType.toUpperCase());
  const abbrev = escapeXml(rType.slice(0, 3).toUpperCase() || '?');
  const iconUrl = safeIconUrl(props.iconUrl);

  if (isContainer) {
    // Drop the redundant `${rType}: ${name}` prefix — when the name
    // itself is informative (e.g. "prod-app-vpc") the resource-type
    // prefix reads as "VPC: VPC" on synthetic names and crowds real
    // ones. The small uppercase type tag sits before the name so
    // readers can tell "prod-app" is a VPC without re-reading.
    const rTypeTag = escapedRType ? `<tspan font-weight="700" fill="#ffffff" opacity="0.75">${escapedRTypeUpper}</tspan>  ` : '';
    return [
      `<rect x="0" y="0" width="${w}" height="${h}" rx="8" ry="8" fill="${fill}" stroke="${accent}" stroke-width="2" stroke-dasharray="8 4" />`,
      `<rect x="0" y="0" width="${w}" height="32" rx="8" ry="8" fill="${accent}" />`,
      `<rect x="0" y="16" width="${w}" height="16" fill="${accent}" />`,
      `<text x="10" y="22" font-size="12" font-weight="600" fill="#ffffff" font-family="Arial, sans-serif">${rTypeTag}${escapedName}</text>`,
    ].join('');
  }

  // M1 stack primitive: if this shape stands for a cluster of resources,
  // render 1–2 offset cards behind the primary card to signal "stack",
  // then draw a count badge in the top-right corner. `stack` is validated
  // by the zod schema above, so we just read .count defensively.
  const stack =
    props.stack && typeof props.stack === 'object'
      ? (props.stack as { count?: number })
      : null;
  const stackCount = stack && typeof stack.count === 'number' && stack.count > 1 ? stack.count : 0;
  const stackFill = lightenHex(fill, 0.08);

  const stackBehind: string[] = [];
  if (stackCount > 0) {
    // Two layered cards behind the primary, offset down-right. Cap at two
    // layers regardless of count — readability matters more than literal
    // accuracy.
    stackBehind.push(
      `<rect x="8" y="8" width="${w}" height="${h - 18}" rx="6" ry="6" fill="${stackFill}" stroke="${stroke}" stroke-width="1" />`,
      `<rect x="4" y="4" width="${w}" height="${h - 18}" rx="6" ry="6" fill="${stackFill}" stroke="${stroke}" stroke-width="1" />`,
    );
  }

  // Icon tile: official icon when available, accent badge fallback otherwise.
  const iconSize = 32;
  const iconX = 10;
  const iconY = Math.round((h - iconSize) / 2);
  const iconCx = iconX + iconSize / 2;
  const iconCy = iconY + iconSize / 2;

  const iconMarkup = iconUrl
    ? [
        `<rect x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" rx="6" ry="6" fill="#ffffff" stroke="${accent}" stroke-width="1.25" />`,
        `<image href="${escapeXml(iconUrl)}" x="${iconX + 5}" y="${iconY + 5}" width="${iconSize - 10}" height="${iconSize - 10}" preserveAspectRatio="xMidYMid meet" />`,
      ].join('')
    : [
        `<rect x="${iconX}" y="${iconY}" width="${iconSize}" height="${iconSize}" rx="6" ry="6" fill="${accent}" />`,
        `<text x="${iconCx}" y="${iconCy + 4}" text-anchor="middle" font-size="10" font-weight="700" fill="#ffffff" font-family="Arial, sans-serif">${abbrev}</text>`,
      ].join('');

  const primary = [
    `<rect x="0" y="0" width="${w}" height="${h}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="1.5" />`,
    `<rect x="0" y="0" width="4" height="${h}" rx="2" ry="2" fill="${accent}" />`,
    iconMarkup,
    `<text x="${iconX + iconSize + 10}" y="${Math.round(h / 2 - 4)}" font-size="12" font-weight="600" fill="${textColor}" font-family="Arial, sans-serif">${escapedName}</text>`,
    `<text x="${iconX + iconSize + 10}" y="${Math.round(h / 2 + 12)}" font-size="10" fill="#64748b" font-family="Arial, sans-serif">${escapedRTypeUpper}</text>`,
  ];

  const countBadge: string[] = [];
  if (stackCount > 0) {
    // stackCount is a validated number from zod; safe to interpolate.
    const label = `×${stackCount}`;
    const approxWidth = 10 + label.length * 6.5;
    const badgeW = Math.max(24, approxWidth);
    const badgeH = 18;
    const badgeX = w - badgeW - 6;
    const badgeY = 6;
    countBadge.push(
      `<rect x="${badgeX}" y="${badgeY}" width="${badgeW}" height="${badgeH}" rx="9" ry="9" fill="${accent}" />`,
      `<text x="${badgeX + badgeW / 2}" y="${badgeY + 13}" text-anchor="middle" font-size="11" font-weight="700" fill="#ffffff" font-family="Arial, sans-serif">${label}</text>`,
    );
  }

  return [...stackBehind, ...primary, ...countBadge].join('');
}

function safeIconUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (value.startsWith('/assets/network-icons/')) return value;
  if (value.startsWith('https://cdn.jsdelivr.net/npm/aws-icons@')) return value;
  return null;
}

/**
 * Lighten a hex colour by blending toward white. Used to tint the
 * stack-rear cards so they read as "behind" the primary card without
 * requiring per-shape colour tables.
 */
function lightenHex(hex: string, amount: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const mix = (c: number) => Math.min(255, Math.round(c + (255 - c) * amount));
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

function createAwsManifest(
  id: string,
  awsServiceId: string,
  label: string,
  dims: { width: number; height: number },
  isContainer = false,
): ShapeManifest<AwsResourceProps> {
  const iconUrl = getIconUrl(awsServiceId);
  const accent = getServiceColor(awsServiceId);
  const semanticType = getSemanticType(awsServiceId);

  return {
    id,
    version: '1.0.0',
    category: 'network',
    icon: 'box',
    propsSchema: awsResourceProps,
    defaultProps: () => ({
      name: label,
      resourceType: awsServiceId,
      semanticType,
      resourceId: '',
      service: awsServiceId,
      hostingProvider: 'aws',
      fillColor: '#FFFFFF',
      strokeColor: '#94a3b8',
      textColor: '#1e293b',
      accentColor: accent,
      iconUrl: iconUrl ?? undefined,
    }),
    defaultDimensions: dims,
    anchors: {
      snap: ['top', 'right', 'bottom', 'left', 'center'],
      perimeter: 'rect',
    },
    capabilities: {
      selectable: true,
      transformable: true,
      textEditable: true,
      connectable: true,
    },
    properties: [
      { key: 'name', label: 'Name', kind: 'string', required: true, ui: { group: 'identity', priority: 1 } },
      { key: 'resourceType', label: 'Type', kind: 'string', ui: { group: 'identity', priority: 2 } },
      { key: 'resourceId', label: 'ARN / ID', kind: 'string', ui: { group: 'identity', priority: 3 } },
      { key: 'region', label: 'Region', kind: 'string', ui: { group: 'identity', priority: 4 } },
    ],
    acl: () => true,
    renderers: {
      svg: (args: { viewModel: unknown; props: unknown }) => renderAwsNodeSvg(
        args.viewModel as { width?: number; height?: number },
        args.props as Record<string, unknown>,
        isContainer,
      ),
    },
  };
}

// Container shapes (VPC, Subnet, Region, AZ, Account)
export const AwsVpcManifest = createAwsManifest('aws_vpc', 'vpc', 'VPC', { width: 400, height: 300 }, true);
export const AwsSubnetManifest = createAwsManifest('aws_subnet', 'subnet', 'Subnet', { width: 350, height: 200 }, true);
// DL-2.5: public/private subnet variants. They share the universal
// `subnet` semantic type so ontology rules continue to match, but they
// render with distinct icons + colors from aws-icon-registry. Useful
// for compose_service prompts that distinguish DMZ vs. application
// vs. database subnets.
export const AwsSubnetPublicManifest = createAwsManifest('aws_subnet_public', 'subnet_public', 'Public Subnet', { width: 350, height: 200 }, true);
export const AwsSubnetPrivateManifest = createAwsManifest('aws_subnet_private', 'subnet_private', 'Private Subnet', { width: 350, height: 200 }, true);
export const AwsRegionManifest = createAwsManifest('aws_region', 'region', 'Region', { width: 500, height: 400 }, true);
export const AwsAccountManifest = createAwsManifest('aws_account', 'account', 'Account', { width: 600, height: 500 }, true);

// Fallback resource — leaf shape, used when an unknown AWS service appears
// in a scan or when the user drops a generic node. Not a container.
export const AwsGenericManifest = createAwsManifest('aws_generic', 'generic', 'AWS Resource', { width: 180, height: 60 });

// Compute
export const AwsEc2Manifest = createAwsManifest('aws_ec2', 'ec2', 'EC2 Instance', { width: 180, height: 60 });
export const AwsLambdaManifest = createAwsManifest('aws_lambda', 'lambda', 'Lambda Function', { width: 180, height: 60 });
export const AwsEcsManifest = createAwsManifest('aws_ecs', 'ecs', 'ECS Service', { width: 180, height: 60 });
export const AwsEksManifest = createAwsManifest('aws_eks', 'eks', 'EKS Cluster', { width: 180, height: 60 });
export const AwsFargateManifest = createAwsManifest('aws_fargate', 'fargate', 'Fargate', { width: 180, height: 60 });

// Load Balancing
export const AwsAlbManifest = createAwsManifest('aws_alb', 'alb', 'App Load Balancer', { width: 180, height: 60 });
export const AwsNlbManifest = createAwsManifest('aws_nlb', 'nlb', 'Network Load Balancer', { width: 180, height: 60 });
export const AwsApiGwManifest = createAwsManifest('aws_api_gateway', 'api_gateway', 'API Gateway', { width: 180, height: 60 });

// Network Edge
export const AwsIgwManifest = createAwsManifest('aws_igw', 'igw', 'Internet Gateway', { width: 140, height: 60 });
export const AwsNatManifest = createAwsManifest('aws_nat', 'nat', 'NAT Gateway', { width: 140, height: 60 });
export const AwsCloudfrontManifest = createAwsManifest('aws_cloudfront', 'cloudfront', 'CloudFront', { width: 180, height: 60 });
export const AwsRoute53Manifest = createAwsManifest('aws_route53', 'route53', 'Route 53', { width: 180, height: 60 });

// Storage
export const AwsS3Manifest = createAwsManifest('aws_s3', 's3', 'S3 Bucket', { width: 180, height: 60 });
export const AwsEbsManifest = createAwsManifest('aws_ebs', 'ebs', 'EBS Volume', { width: 180, height: 60 });
export const AwsEfsManifest = createAwsManifest('aws_efs', 'efs', 'EFS', { width: 180, height: 60 });

// Database
export const AwsRdsManifest = createAwsManifest('aws_rds', 'rds', 'RDS Instance', { width: 180, height: 60 });
export const AwsRdsPostgresManifest = createAwsManifest('aws_rds_postgres', 'rds_postgres', 'RDS Postgres', { width: 180, height: 60 });
export const AwsRdsMysqlManifest = createAwsManifest('aws_rds_mysql', 'rds_mysql', 'RDS MySQL', { width: 180, height: 60 });
export const AwsAuroraPostgresManifest = createAwsManifest('aws_aurora_postgres', 'aurora_postgres', 'Aurora Postgres', { width: 180, height: 60 });
export const AwsAuroraMysqlManifest = createAwsManifest('aws_aurora_mysql', 'aurora_mysql', 'Aurora MySQL', { width: 180, height: 60 });
export const AwsDynamoDbManifest = createAwsManifest('aws_dynamodb', 'dynamodb', 'DynamoDB', { width: 180, height: 60 });
export const AwsDocumentDbManifest = createAwsManifest('aws_documentdb', 'documentdb', 'DocumentDB (MongoDB)', { width: 180, height: 60 });
export const AwsElastiCacheRedisManifest = createAwsManifest('aws_elasticache_redis', 'elasticache_redis', 'ElastiCache Redis', { width: 180, height: 60 });
export const AwsElastiCacheMemcachedManifest = createAwsManifest('aws_elasticache_memcached', 'elasticache_memcached', 'ElastiCache Memcached', { width: 180, height: 60 });
export const AwsRedshiftManifest = createAwsManifest('aws_redshift', 'redshift', 'Redshift', { width: 180, height: 60 });
export const AwsNeptuneManifest = createAwsManifest('aws_neptune', 'neptune', 'Neptune', { width: 180, height: 60 });
export const AwsOpenSearchManifest = createAwsManifest('aws_opensearch', 'opensearch', 'OpenSearch', { width: 180, height: 60 });

// Identity
export const AwsIamRoleManifest = createAwsManifest('aws_iam_role', 'iam_role', 'IAM Role', { width: 180, height: 60 });
export const AwsIamUserManifest = createAwsManifest('aws_iam_user', 'iam_user', 'IAM User', { width: 180, height: 60 });
export const AwsCognitoManifest = createAwsManifest('aws_cognito', 'cognito', 'Cognito', { width: 180, height: 60 });

// Security
export const AwsSgManifest = createAwsManifest('aws_sg', 'sg', 'Security Group', { width: 180, height: 60 });
export const AwsKmsManifest = createAwsManifest('aws_kms', 'kms', 'KMS Key', { width: 180, height: 60 });
export const AwsWafManifest = createAwsManifest('aws_waf', 'waf', 'WAF', { width: 180, height: 60 });
export const AwsSecretsManagerManifest = createAwsManifest('aws_secrets_manager', 'secrets_manager', 'Secrets Manager', { width: 180, height: 60 });

// Messaging
export const AwsSnsManifest = createAwsManifest('aws_sns', 'sns', 'SNS Topic', { width: 180, height: 60 });
export const AwsSqsManifest = createAwsManifest('aws_sqs', 'sqs', 'SQS Queue', { width: 180, height: 60 });
export const AwsEventBridgeManifest = createAwsManifest('aws_eventbridge', 'eventbridge', 'EventBridge', { width: 180, height: 60 });

// Management
export const AwsCloudWatchManifest = createAwsManifest('aws_cloudwatch', 'cloudwatch', 'CloudWatch', { width: 180, height: 60 });
export const AwsCloudTrailManifest = createAwsManifest('aws_cloudtrail', 'cloudtrail', 'CloudTrail', { width: 180, height: 60 });
export const AwsCloudWatchAlarmManifest = createAwsManifest('aws_cloudwatch_alarm', 'cloudwatch_alarm', 'CloudWatch Alarm', { width: 180, height: 60 });
export const AwsAutoScalingGroupManifest = createAwsManifest('aws_autoscaling_group', 'autoscaling_group', 'Auto Scaling Group', { width: 180, height: 60 });

// Networking extras (route tables, ALB sub-resources, DNS records, certificates)
export const AwsRouteTableManifest = createAwsManifest('aws_route_table', 'route_table', 'Route Table', { width: 180, height: 60 });
export const AwsAlbListenerManifest = createAwsManifest('aws_alb_listener', 'alb_listener', 'ALB Listener', { width: 180, height: 60 });
export const AwsTargetGroupManifest = createAwsManifest('aws_target_group', 'target_group', 'Target Group', { width: 180, height: 60 });
export const AwsRoute53RecordManifest = createAwsManifest('aws_route53_record', 'route53_record', 'Route 53 Record', { width: 180, height: 60 });
export const AwsAcmCertManifest = createAwsManifest('aws_acm_cert', 'acm', 'ACM Certificate', { width: 180, height: 60 });

export const AwsShapeManifests = [
  // Containers
  AwsVpcManifest, AwsSubnetManifest, AwsSubnetPublicManifest, AwsSubnetPrivateManifest, AwsRegionManifest, AwsAccountManifest,
  // Compute
  AwsEc2Manifest, AwsLambdaManifest, AwsEcsManifest, AwsEksManifest, AwsFargateManifest,
  // Load Balancing
  AwsAlbManifest, AwsNlbManifest, AwsApiGwManifest, AwsAlbListenerManifest, AwsTargetGroupManifest,
  // Network Edge
  AwsIgwManifest, AwsNatManifest, AwsCloudfrontManifest, AwsRoute53Manifest,
  AwsRoute53RecordManifest, AwsRouteTableManifest, AwsAcmCertManifest,
  // Storage
  AwsS3Manifest, AwsEbsManifest, AwsEfsManifest,
  // Database
  AwsRdsManifest, AwsRdsPostgresManifest, AwsRdsMysqlManifest,
  AwsAuroraPostgresManifest, AwsAuroraMysqlManifest,
  AwsDynamoDbManifest, AwsDocumentDbManifest,
  AwsElastiCacheRedisManifest, AwsElastiCacheMemcachedManifest,
  AwsRedshiftManifest, AwsNeptuneManifest, AwsOpenSearchManifest,
  // Identity
  AwsIamRoleManifest, AwsIamUserManifest, AwsCognitoManifest,
  // Security
  AwsSgManifest, AwsKmsManifest, AwsWafManifest, AwsSecretsManagerManifest,
  // Messaging
  AwsSnsManifest, AwsSqsManifest, AwsEventBridgeManifest,
  // Management
  AwsCloudWatchManifest, AwsCloudTrailManifest, AwsCloudWatchAlarmManifest, AwsAutoScalingGroupManifest,
  // Fallback (not a container — leaf shape for unknown AWS services)
  AwsGenericManifest,
];
